import React, { useCallback, useEffect, useState } from 'react';
import { Pencil, UserX } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import '../../component-styles/TeamSettings.css';
import API_BASE from '../../utils/api.js';
import { useAuth } from '../../auth/useAuth';
import { SEAT_PRICE_LABEL, PREMIUM_INCLUDED_SEATS } from '../../utils/seatPricing';
import AddSeatModal from '../Stripe/AddSeatModal';

export default function TeamSettings() {
  const apiUrl = API_BASE;
  const { auth } = useAuth();
  const navigate = useNavigate();

  const userId = localStorage.getItem('userId');

  const [team, setTeam] = useState(null); // null = not loaded yet
  const [loading, setLoading] = useState(true);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [busyEmail, setBusyEmail] = useState('');
  const [removingUserId, setRemovingUserId] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [settingsPlan, setSettingsPlan] = useState('');
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [bonusMaxSeats, setBonusMaxSeats] = useState(0);
  const [bonusFreeSeats, setBonusFreeSeats] = useState(0);
  const [linkedSeatType, setLinkedSeatType] = useState('');
  const [linkedAccountOwnerId, setLinkedAccountOwnerId] = useState('');
  const [seats, setSeats] = useState([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [pendingSeatEmail, setPendingSeatEmail] = useState('');
  const [seatPreview, setSeatPreview] = useState(null);
  const [showAddSeatModal, setShowAddSeatModal] = useState(false);

  const [showActiveOnly, setShowActiveOnly] = useState(true);

  const [formData, setFormData] = useState({
    name: '',
    role: '',
    cost: '',
    date: '',
    email: '',
    phone: '',
    street: '',
    city: '',
    state: '',
    zip: '',
  });

  const loadSettings = useCallback(async () => {
    if (!userId) return;
    const res = await fetch(`${apiUrl}/api/settings/${userId}`);
    const data = await res.json();

    setTeam(data.team || []);
    setSettingsPlan(data.plan || '');
    setBillingCycle(data.billing === 'annual' ? 'annual' : 'monthly');
    setBonusMaxSeats(Number(data.bonusMaxSeats) || 0);
    setBonusFreeSeats(Number(data.bonusFreeSeats) || 0);
    setLinkedSeatType(data.linkedSeatType || '');
    setLinkedAccountOwnerId(data.linkedAccountOwnerId || '');
    setSeats(Array.isArray(data.additionalSeats) ? data.additionalSeats : []);
    setLoading(false);
  }, [apiUrl, userId]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  async function copyTextToClipboard(value) {
    const text = String(value || '');
    if (!text) return;

    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }

    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
  }

  function openNew() {
    if (loading || !team) return;


    setEditingIndex(null);
    setFormData({
      name: '',
      role: '',
      cost: '',
      date: '',
      email: '',
      phone: '',
      street: '',
      city: '',
      state: '',
      zip: '',
    });
    setShowModal(true);
  }

  function openEdit(idx) {
    if (loading || !team) return;
    navigate(`/settings/team/${idx}`);
  }

  async function handleSave(e) {
    e.preventDefault();


    let updated;
    if (editingIndex === null) {
      updated = [...team, { ...formData }];
    } else {
      updated = [...team];
      updated[editingIndex] = { ...formData };
    }


    const res = await fetch(`${apiUrl}/api/settings/${userId}/team`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ team: updated }),
    });

    await res.text();

    setTeam(updated);
    setShowModal(false);
  }

  async function handleToggleActive(idx) {
    const updated = [...team];
    updated[idx] = { ...updated[idx], active: updated[idx].active === false ? true : false };

    await fetch(`${apiUrl}/api/settings/${userId}/team`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ team: updated }),
    });

    setTeam(updated);
  }

  // Creates or resends an invite for `email`. Resending an existing pending
  // invite (free or already-paid) never charges again — the backend detects
  // that case itself. Only used for the free path / resend / copy-link;
  // brand-new paid seats go through AddSeatModal instead.
  async function sendInvite(email, { sendEmail }) {
    const res = await fetch(`${apiUrl}/api/auth/additional-user-invite`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${auth.idToken}`,
      },
      body: JSON.stringify({ email, sendEmail }),
    });

    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.error || 'Failed to send invite');

    setInviteMessage(
      data?.emailSent === false
        ? 'Invite created, but email delivery failed. Use Copy Invite Link.'
        : 'Invitation sent.'
    );
    await loadSettings();
    return data;
  }

  async function handleInviteSubmit() {
    const email = inviteEmail.trim();
    if (!email || !auth?.idToken) return;
    setInviteMessage('');
    setInviteLoading(true);

    try {
      const previewRes = await fetch(`${apiUrl}/api/stripe/preview-seat`, {
        headers: { Authorization: `Bearer ${auth.idToken}` },
      });
      const previewData = await previewRes.json().catch(() => ({}));
      if (!previewRes.ok) throw new Error(previewData?.error || 'Failed to check seat pricing');

      if (previewData.isFree) {
        await sendInvite(email, { sendEmail: true });
        setInviteEmail('');
      } else {
        setPendingSeatEmail(email);
        setSeatPreview(previewData);
        setShowAddSeatModal(true);
      }
    } catch (err) {
      setInviteMessage(err.message || 'Failed to send invite');
    } finally {
      setInviteLoading(false);
    }
  }

  async function handleResendInvite(email) {
    if (!auth?.idToken) return;
    setBusyEmail(email);
    setInviteMessage('');
    try {
      await sendInvite(email, { sendEmail: true });
    } catch (err) {
      setInviteMessage(err.message || 'Failed to resend invite');
    } finally {
      setBusyEmail('');
    }
  }

  async function handleCopyInviteLink(email) {
    if (!auth?.idToken) return;
    setBusyEmail(email);
    setInviteMessage('');
    try {
      const data = await sendInvite(email, { sendEmail: false });
      const inviteLink = String(data?.inviteLink || '').trim();
      if (!inviteLink) throw new Error('Invite link unavailable');
      await copyTextToClipboard(inviteLink);
      setInviteMessage('Invite link copied.');
    } catch (err) {
      setInviteMessage(err.message || 'Failed to copy invite link');
    } finally {
      setBusyEmail('');
    }
  }

  async function handleCancelInvite(email) {
    if (!auth?.idToken) return;
    setBusyEmail(email);
    setInviteMessage('');
    try {
      const res = await fetch(
        `${apiUrl}/api/auth/additional-user-invite?email=${encodeURIComponent(email)}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${auth.idToken}` },
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to cancel invite');
      setInviteMessage('Pending invite cancelled.');
      await loadSettings();
    } catch (err) {
      setInviteMessage(err.message || 'Failed to cancel invite');
    } finally {
      setBusyEmail('');
    }
  }

  async function handleRemoveSeat(seatUserId) {
    if (!auth?.idToken || !seatUserId) return;
    if (
      !window.confirm(
        "Remove this teammate's access? They would need a new invite to rejoin."
      )
    ) {
      return;
    }

    setRemovingUserId(seatUserId);
    setInviteMessage('');
    try {
      const res = await fetch(
        `${apiUrl}/api/auth/additional-seats/${encodeURIComponent(seatUserId)}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${auth.idToken}` },
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to remove seat');
      setInviteMessage('Seat removed.');
      await loadSettings();
    } catch (err) {
      setInviteMessage(err.message || 'Failed to remove seat');
    } finally {
      setRemovingUserId('');
    }
  }

  function handleAddSeatSuccess(result) {
    setShowAddSeatModal(false);
    setPendingSeatEmail('');
    setSeatPreview(null);
    setInviteEmail('');
    setInviteMessage(
      result?.emailSent === false
        ? 'Seat added, but invite email delivery failed. Use Copy Invite Link.'
        : 'Seat added and invitation sent.'
    );
    loadSettings();
  }

  const isLinkedAdditionalSeat =
    linkedSeatType === 'additional_user' || Boolean(linkedAccountOwnerId);
  const activeSeatsForDisplay = seats.filter(
    (s) => s.status === 'pending' || s.status === 'claimed'
  );
  // Mirrors getIncludedSeatCount in backend/src/utils/additionalSeats.js.
  const includedSeats =
    settingsPlan === 'premium' || bonusMaxSeats > 0 ? PREMIUM_INCLUDED_SEATS + bonusFreeSeats : 0;
  const seatPrice = SEAT_PRICE_LABEL[billingCycle];

  return (
    <div className="team-settings">
      <div className="team-header">
        <button
          className={`team-filter-btn ${showActiveOnly ? 'active' : ''}`}
          onClick={() => setShowActiveOnly((v) => !v)}
          type="button"
        >
          {showActiveOnly ? 'Active Only' : 'All Members'}
        </button>
        <button className="add-team-btn" onClick={openNew} disabled={loading}>
          + Add New
        </button>
      </div>

      <div className="additional-seat-panel">
        <h4>Additional Seats</h4>

        {isLinkedAdditionalSeat ? (
          <p className="seat-status">
            This account is using an additional-user seat. Invite management is available to the
            primary account owner.
          </p>
        ) : (
          <>
            <p className="seat-status">
              {includedSeats > 0
                ? `Your plan includes ${includedSeats} additional seats. Each seat after that is ${seatPrice}, billed on your subscription.`
                : `Each additional seat is ${seatPrice}, billed on your subscription. Premium includes ${PREMIUM_INCLUDED_SEATS} seats at no extra cost.`}
            </p>

            {activeSeatsForDisplay.length > 0 && (
              <ul className="seat-list">
                {activeSeatsForDisplay.map((seat) => {
                  const key = seat.userId || seat.invitedEmail;
                  return (
                    <li key={key} className="seat-list-item">
                      <div className="seat-list-info">
                        <span className="seat-list-email">{seat.invitedEmail}</span>
                        <span className={`seat-list-badge seat-list-badge--${seat.status}`}>
                          {seat.status === 'claimed' ? 'Active' : 'Invited'}
                        </span>
                        {!seat.isFree && (
                          <span className="seat-list-badge seat-list-badge--paid">Paid</span>
                        )}
                      </div>
                      <div className="seat-list-actions">
                        {seat.status === 'pending' && (
                          <>
                            <button
                              type="button"
                              className="invite-secondary-btn"
                              onClick={() => handleResendInvite(seat.invitedEmail)}
                              disabled={busyEmail === seat.invitedEmail}
                            >
                              {busyEmail === seat.invitedEmail ? 'Working…' : 'Resend'}
                            </button>
                            <button
                              type="button"
                              className="invite-secondary-btn"
                              onClick={() => handleCopyInviteLink(seat.invitedEmail)}
                              disabled={busyEmail === seat.invitedEmail}
                            >
                              Copy Link
                            </button>
                            <button
                              type="button"
                              className="invite-cancel-btn"
                              onClick={() => handleCancelInvite(seat.invitedEmail)}
                              disabled={busyEmail === seat.invitedEmail}
                            >
                              Cancel
                            </button>
                          </>
                        )}
                        {seat.status === 'claimed' && (
                          <button
                            type="button"
                            className="invite-cancel-btn"
                            onClick={() => handleRemoveSeat(seat.userId)}
                            disabled={removingUserId === seat.userId}
                          >
                            {removingUserId === seat.userId ? 'Removing…' : 'Remove'}
                          </button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}

            <div className="invite-row">
              <input
                type="email"
                placeholder="teammate@company.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
              />
              <button
                type="button"
                className="add-team-btn"
                onClick={handleInviteSubmit}
                disabled={inviteLoading || !inviteEmail.trim()}
              >
                {inviteLoading ? 'Checking…' : 'Send Invite'}
              </button>
            </div>
            {inviteMessage && <p className="invite-message">{inviteMessage}</p>}
          </>
        )}
      </div>

      <div className="team-table">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Role</th>
              <th>Cost Rate</th>
              <th>Start Date</th>
              <th>Location</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {!loading &&
              team &&
              team
                .filter((m) => !showActiveOnly || m.active !== false)
                .map((m, idx) => {
                  const realIdx = team.indexOf(m);
                  return (
                    <tr key={idx}>
                      <td>{m.name}</td>
                      <td>{m.role}</td>
                      <td>${m.cost}</td>
                      <td>{m.date}</td>
                      <td>{m.state}</td>
                      <td className="action-cell">
                        <button
                          className="action-icon-btn"
                          onClick={() => openEdit(realIdx)}
                          title="Edit"
                        >
                          <Pencil size={14} color="#007673" />
                        </button>
                        <button
                          className="action-icon-btn"
                          onClick={() => handleToggleActive(realIdx)}
                          title={m.active === false ? 'Reactivate' : 'Deactivate'}
                        >
                          <UserX size={14} color={m.active === false ? '#6b7280' : '#dc2626'} />
                        </button>
                      </td>
                    </tr>
                  );
                })}

            {loading && (
              <tr>
                <td colSpan="6" style={{ textAlign: 'center', opacity: 0.6 }}>
                  Loading team...
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="team-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="team-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{editingIndex === null ? 'New Team Member' : 'Edit Team Member'}</h3>

            <form className="team-form" onSubmit={handleSave}>
              <label>Name</label>
              <input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />

              <label>Role</label>
              <input
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                required
              />

              <label>Start Date</label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                required
              />

              <label>Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />

              <label>Phone</label>
              <input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />

              <label>Street</label>
              <input
                value={formData.street}
                onChange={(e) => setFormData({ ...formData, street: e.target.value })}
              />

              <div className="team-address-row">
                <div className="team-address-col">
                  <label>City</label>
                  <input
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  />
                </div>
                <div className="team-address-col">
                  <label>State</label>
                  <input
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  />
                </div>
                <div className="team-address-col">
                  <label>Zip</label>
                  <input
                    value={formData.zip}
                    onChange={(e) => setFormData({ ...formData, zip: e.target.value })}
                  />
                </div>
              </div>

              <label>Cost Rate</label>
              <input
                type="number"
                value={formData.cost}
                onChange={(e) => setFormData({ ...formData, cost: e.target.value })}
                required
              />

              <div className="form-actions">
                <button
                  type="button"
                  className="team-cancel-btn"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="team-save-btn" disabled={loading}>
                  {editingIndex === null ? 'Save' : 'Update'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showAddSeatModal && (
        <AddSeatModal
          invitedEmail={pendingSeatEmail}
          preview={seatPreview}
          onClose={() => {
            setShowAddSeatModal(false);
            setPendingSeatEmail('');
            setSeatPreview(null);
          }}
          onSuccess={handleAddSeatSuccess}
        />
      )}
    </div>
  );
}
